import React, { useMemo } from 'react';
import { Card, Button, Alert } from 'antd';
import { OrderSummaryProps } from '../interfaces';
import Spinner from '../spinner';
import { useAppSelector } from '../../hooks/redux';
import SalesTaxEstimator from '../salesTaxEstimator/salesTaxEstimator';
import { useLocation } from 'react-router-dom';
import { getCheckoutTotal, getSalesTaxDisplayValue } from '../../config/salesTax';
import './orderSummary.scss';

const OrderSummary: React.FC<OrderSummaryProps> = ({
  handleNavigate,
  totalAmount,
  isPricingLoading = false,
  isCheckoutDisabled = false,
  checkoutDisabledMessage,
}) => {
  const location = useLocation();
  const status = useAppSelector((state) => state.shoppingCart.status);
  const taxAmount = useAppSelector((state) => state.shoppingCart.taxAmount);
  const taxStatus = useAppSelector((state) => state.shoppingCart.taxStatus);
  const showTaxEstimator = location.pathname === '/cart';

  const totalWithTax = getCheckoutTotal(totalAmount, taxAmount);
  const paymentControlsDisabled = isPricingLoading || isCheckoutDisabled;
  const isSummaryLoading = useMemo(
    () =>
      status === 'idle' ||
      status === 'loading' ||
      isPricingLoading,
    [isPricingLoading, status]
  );
  const salesTaxDisplayValue = getSalesTaxDisplayValue({ taxAmount, taxStatus });

  if (status === 'failed') return <Alert message="Error loading summary" type="error" />;

  return (
    <Card
      title="Order Summary"
      className={`order-summary${isSummaryLoading ? ' order-summary--loading' : ''}`}
    >
      {isSummaryLoading ? (
        <div
          className="order-summary-loading"
          role="status"
          aria-live="polite"
          aria-label="Loading order summary"
        >
          <Spinner />
        </div>
      ) : (
        <>
          {isCheckoutDisabled && checkoutDisabledMessage && (
            <Alert
              className="order-summary-alert"
              message={checkoutDisabledMessage}
              type="info"
              showIcon
            />
          )}
          <div className="order-summary-detail">
            <p>Subtotal:</p>
            <p>{`$${totalAmount.toFixed(2)}`}</p>
          </div>
          <div className="order-summary-detail">
            <p>FedEx Shipping:</p>
            <p>Free</p>
          </div>
          {showTaxEstimator && <SalesTaxEstimator />}
          <div className="order-summary-detail">
            <p>Sales Tax Estimate:</p>
            <p>{salesTaxDisplayValue}</p>
          </div>
          <div className="order-summary-detail">
            <p>Total:</p>
            <p>{`$${totalWithTax.toFixed(2)}`}</p>
          </div>
          <Button
            type="primary"
            className="order-summary-checkout-button"
            onClick={handleNavigate}
            disabled={paymentControlsDisabled}
          >
            Checkout
          </Button>
        </>
      )}
    </Card>
  );
};

export default OrderSummary;
